// The fence state of an account transition (src/lib/accountChannel.ts owns
// the cross-tab messages). Split out so i18n/apiFetch.ts can hold API
// requests while fenced without pulling the channel code into the prerender
// graph: module scope here is plain `let`s and functions, and localStorage is
// read only inside function bodies (src/__tests__/prerenderGraph.test.ts).

/** localStorage: {from, at} while a transition on this browser is in progress. */
export const TRANSITION_MARKER_KEY = 'cb-account-transition'
/** A marker older than this is a crashed transition, not a live one. */
const MARKER_MAX_AGE_MS = 30_000

let fenced = false
let unfenceWaiters: Array<() => void> = []
// This tab started the transition in progress: its own requests (the
// sign-out call itself) must never wait on the marker it wrote.
let initiating = false

/** True while a peer's transition away from the owner this tab shows is in progress. */
export function isAccountFenced(): boolean {
  return fenced
}

export function setFenced(): void {
  fenced = true
}

/** Resolves when the fence lifts (it never does if the tab reloads first). */
export function whenUnfenced(): Promise<void> {
  if (!fenced) return Promise.resolve()
  return new Promise(resolve => { unfenceWaiters.push(resolve) })
}

export function liftFence(): void {
  fenced = false
  const waiters = unfenceWaiters
  unfenceWaiters = []
  for (const w of waiters) w()
}

export function setInitiating(value: boolean): void {
  initiating = value
}

export function writeMarker(from: string | null): void {
  try {
    if (from === null) localStorage.removeItem(TRANSITION_MARKER_KEY)
    else localStorage.setItem(TRANSITION_MARKER_KEY, JSON.stringify({ from, at: Date.now() }))
  } catch {
    // No storage: the channel message alone still fences the open peers.
  }
}

/** A live transition marker (any tab), or null. */
function liveMarker(): { from: string; at: number } | null {
  try {
    const raw = localStorage.getItem(TRANSITION_MARKER_KEY)
    if (!raw) return null
    const m = JSON.parse(raw) as { from?: unknown; at?: unknown }
    if (typeof m.from !== 'string' || typeof m.at !== 'number') return null
    return Date.now() - m.at < MARKER_MAX_AGE_MS ? { from: m.from, at: m.at } : null
  } catch {
    return null
  }
}

/** A live transition marker exists (any tab). */
export function hasAccountTransitionInProgress(): boolean {
  return liveMarker() !== null
}

/** A transition started by ANOTHER tab is in progress. */
export function hasForeignAccountTransition(): boolean {
  return !initiating && liveMarker() !== null
}

/**
 * Before bootstrap (main.tsx), and for every request of a tab that booted
 * mid-transition: wait for another tab's transition to finish (marker
 * cleared) so this tab never boots or uses the departing session out of
 * shared storage. Bounded by `maxMs`; with no marker it resolves at once.
 */
export async function waitForAccountTransition({ maxMs = 10_000, pollMs = 100 } = {}): Promise<void> {
  const until = Date.now() + maxMs
  while (liveMarker() !== null && Date.now() < until) {
    await new Promise(r => setTimeout(r, pollMs))
  }
}

/** Test seam: waiters are dropped, never released into the next test. */
export function __resetAccountFenceForTests(): void {
  initiating = false
  unfenceWaiters = []
  liftFence()
}
